import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
} from '@mui/material';
import {
  Twitter,
  Telegram,
  GitHub,
  LinkedIn,
  Instagram,
  Security,
  Link as LinkIcon,
  Lock,
  Shield,
  CheckCircle,
  Email,
  Phone,
  LocationOn,
  ArrowUpward,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { motion } from 'framer-motion';
import { colors, typography, spacing, borderRadius, shadows, transitions, componentStyles } from '../../styles/designSystem';
import newLogo from '../../assets/newLogo.png';

const useStyles = makeStyles((theme) => ({
  footer: {
    background: colors.gradients.hero,
    borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
    padding: `${spacing['4xl']} 0 ${spacing.xl}`,
    marginTop: spacing['5xl'],
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 30% 70%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 70% 30%, rgba(255, 235, 59, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  container: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  logo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.lg,
    transition: transitions.smooth,
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  
  logoImage: {
    width: 50,
    height: 50,
    marginRight: spacing.md,
    borderRadius: borderRadius.md,
  },
  
  logoText: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['2xl'],
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: typography.letterSpacing.tight,
  },
  
  description: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.lg,
    color: colors.text.secondary,
    lineHeight: typography.lineHeight.relaxed,
    marginBottom: spacing.xl,
    maxWidth: '400px',
    letterSpacing: typography.letterSpacing.normal,
  },
  
  socialLinks: {
    display: 'flex',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  
  socialButton: {
    width: 56,
    height: 56,
    borderRadius: borderRadius.xl,
    background: colors.gradients.glass,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    color: colors.text.primary,
    transition: transitions.smooth,
    backdropFilter: 'blur(10px)',
    '&:hover': {
      background: colors.primary.main,
      borderColor: colors.primary.main,
      transform: 'translateY(-4px) scale(1.1)',
      boxShadow: shadows.glow,
    },
  },
  
  sectionTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.xl,
    color: colors.text.primary,
    marginBottom: spacing.lg,
    letterSpacing: typography.letterSpacing.wide,
  },
  
  linkList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  
  linkItem: {
    marginBottom: spacing.md,
  },
  
  footerLink: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    textDecoration: 'none',
    transition: transitions.smooth,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.xs,
    borderRadius: borderRadius.md,
    '&:hover': {
      color: colors.primary.main,
      background: 'rgba(230, 0, 122, 0.1)',
      transform: 'translateX(4px)',
    },
  },
  
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
  },
  
  trustSection: {
    background: colors.gradients.glass,
    borderRadius: borderRadius['2xl'],
    padding: spacing.xl,
    marginTop: spacing['3xl'],
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    backdropFilter: 'blur(20px)',
  },
  
  trustTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize['2xl'],
    color: colors.text.primary,
    marginBottom: spacing.lg,
    textAlign: 'center',
    letterSpacing: typography.letterSpacing.wide,
  },
  
  trustGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.lg,
  },
  
  trustCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.xl,
    padding: spacing.lg,
    textAlign: 'center',
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    transition: transitions.smooth,
    height: '100%',
    minHeight: '200px', // Add consistent minimum height
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.1)',
      borderColor: colors.primary.main,
      transform: 'translateY(-4px)',
    },
  },
  
  trustIcon: {
    fontSize: '2.5rem',
    color: colors.primary.main,
    marginBottom: spacing.md,
  },
  
  trustCardTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.lg,
    color: colors.text.primary,
    marginBottom: spacing.sm,
    letterSpacing: typography.letterSpacing.wide,
  },
  
  trustCardDescription: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    lineHeight: typography.lineHeight.relaxed,
  },
  
  bottomSection: {
    borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
    paddingTop: spacing.xl,
    marginTop: spacing['3xl'],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  
  copyright: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  
  scrollToTop: {
    position: 'fixed',
    bottom: spacing.xl,
    right: spacing.xl,
    width: 56,
    height: 56,
    borderRadius: borderRadius.full,
    background: colors.gradients.primary,
    color: colors.text.primary,
    border: 'none',
    cursor: 'pointer',
    transition: transitions.smooth,
    boxShadow: shadows.glass,
    backdropFilter: 'blur(10px)',
    zIndex: 1000,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.glow,
    },
  },
}));

const ModernFooter = () => {
  const classes = useStyles();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const socialLinks = [
    { icon: <Twitter />, url: '#', label: 'Twitter' },
    { icon: <Telegram />, url: '#', label: 'Telegram' },
    { icon: <GitHub />, url: '#', label: 'GitHub' },
    { icon: <LinkedIn />, url: '#', label: 'LinkedIn' },
    { icon: <Instagram />, url: '#', label: 'Instagram' },
  ];

  const quickLinks = [
    { label: 'Home', url: '/' },
    { label: 'Whitepaper', url: '/whitepaper' },
    { label: 'Tokenomics', url: '#' },
    { label: 'Roadmap', url: '#' },
  ];

  const supportLinks = [
    { label: 'Help Center', url: '#' },
    { label: 'Documentation', url: '#' },
    { label: 'Community', url: '#' },
    { label: 'Contact', url: '#' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', url: '#' },
    { label: 'Terms of Service', url: '#' },
    { label: 'Cookie Policy', url: '#' },
    { label: 'Disclaimer', url: '#' },
  ];

  const trustFeatures = [
    {
      icon: <Security />,
      title: 'Audited Smart Contracts',
      description: 'All contracts verified and audited by leading security firms',
    },
    {
      icon: <Lock />,
      title: 'Liquidity Locked',
      description: 'Liquidity locked for 1 year minimum to ensure stability',
    },
    {
      icon: <Shield />,
      title: 'Team Tokens Locked',
      description: 'Team tokens locked with gradual vesting schedule',
    },
    {
      icon: <CheckCircle />,
      title: 'No Mint Function',
      description: 'Fixed supply with no ability to create additional tokens',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const renderLinkSection = (title, links) => (
    <motion.div variants={itemVariants}>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      <ul className={classes.linkList}>
        {links.map((link, index) => (
          <li key={index} className={classes.linkItem}>
            <Link href={link.url} className={classes.footerLink}>
              <LinkIcon fontSize="small" />
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </motion.div>
  );

  return (
    <Box className={classes.footer}>
      <Container className={classes.container}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Box className={classes.logo}>
                  <img src={newLogo} alt="Retro Llama" className={classes.logoImage} />
                  <Typography className={classes.logoText}>Retro Llama</Typography>
                </Box>
                <Typography className={classes.description}>
                  Experience the future of decentralized trading with our innovative token platform. 
                  Built for security, performance, and community-driven growth.
                </Typography>
                <Box className={classes.socialLinks}>
                  {socialLinks.map((social, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <IconButton
                        className={classes.socialButton}
                        href={social.url}
                        aria-label={social.label}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              {renderLinkSection('Quick Links', quickLinks)}
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              {renderLinkSection('Support', supportLinks)}
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              {renderLinkSection('Legal', legalLinks)}
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <motion.div variants={itemVariants}>
                <Typography className={classes.sectionTitle}>Contact</Typography>
                <Box className={classes.contactInfo}>
                  <Email fontSize="small" />
                  <span>info@retrollama.com</span>
                </Box>
                <Box className={classes.contactInfo}>
                  <Phone fontSize="small" />
                  <span>+1 (555) 123-4567</span>
                </Box>
                <Box className={classes.contactInfo}>
                  <LocationOn fontSize="small" />
                  <span>Global Community</span>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          <motion.div variants={itemVariants}>
            <Box className={classes.trustSection}>
              <Typography className={classes.trustTitle}>
                Why Trust Retro Llama Token?
              </Typography>
              <Grid className={classes.trustGrid} style={{ alignItems: 'stretch' }}>
                {trustFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ display: 'flex', height: '100%' }}
                  >
                    <Box className={classes.trustCard}>
                      <Box className={classes.trustIcon}>
                        {feature.icon}
                      </Box>
                      <Typography className={classes.trustCardTitle}>
                        {feature.title}
                      </Typography>
                      <Typography className={classes.trustCardDescription}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Grid>
            </Box>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Box className={classes.bottomSection}>
              <Typography className={classes.copyright}>
                © 2024 Retro Llama Token. All rights reserved.
              </Typography>
              <Box className={classes.socialLinks}>
                {socialLinks.slice(0, 3).map((social, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      className={classes.socialButton}
                      href={social.url}
                      aria-label={social.label}
                      size="small"
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      <motion.button
        className={classes.scrollToTop}
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <ArrowUpward />
      </motion.button>
    </Box>
  );
};

export default ModernFooter; 